import type { CSSProperties } from "react";
import { ConnectButton } from "./connect-button";

const TOAST_COLOR = "#f59e0b";

function SimulateButton({
  onClick,
  compact = false,
}: {
  onClick?: () => void;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontFamily: "var(--f-mono)",
        fontSize: 10,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        fontWeight: 600,
        padding: compact ? "7px 10px" : "7px 12px",
        background: "transparent",
        color: "var(--brand)",
        border: "1px solid var(--brand)",
        cursor: onClick ? "pointer" : "default",
        borderRadius: 2,
        whiteSpace: "nowrap",
      }}
      onMouseEnter={(e) => {
        if (!onClick) return;
        e.currentTarget.style.background = "rgba(158, 203, 255, 0.08)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      Simulate
    </button>
  );
}

function BetaNotice() {
  const wrap: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "6px 12px",
    background: "rgba(30, 20, 6, 0.92)",
    border: `1px solid ${TOAST_COLOR}66`,
    boxShadow: `0 0 0 1px ${TOAST_COLOR}22 inset`,
    borderRadius: 4,
    whiteSpace: "nowrap",
  };
  return (
    <div style={wrap} role="status" aria-live="polite">
      <span
        aria-hidden
        style={{
          width: 6,
          height: 6,
          borderRadius: 3,
          background: TOAST_COLOR,
          boxShadow: `0 0 8px ${TOAST_COLOR}`,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 9,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          fontWeight: 600,
          color: TOAST_COLOR,
        }}
      >
        BETA
      </span>
      <span
        aria-hidden
        style={{
          width: 1,
          height: 12,
          background: `${TOAST_COLOR}44`,
        }}
      />
      <span
        style={{
          fontFamily: "var(--f-sans)",
          fontSize: 12,
          color: "var(--fg-max)",
        }}
      >
        Currently limited to Perps × Predictions (1 each)
      </span>
    </div>
  );
}

function CompositeLogo({ height = 30 }: { height?: number }) {
  return (
    <svg
      aria-label="Composite Derivatives"
      viewBox="0 0 256 84"
      height={height}
      width={(256 / 84) * height}
      style={{ display: "block" }}
    >
      <text
        x="0"
        y="34"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
        fontSize="40"
        fontWeight="500"
        letterSpacing="-1.2"
        fill="#fafafa"
      >
        composite
      </text>
      <text
        x="0"
        y="76"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
        fontSize="40"
        fontWeight="400"
        letterSpacing="-1.2"
        fill="#888"
      >
        derivatives
      </text>
    </svg>
  );
}

export function TopBar({
  compact = false,
  onSimulate,
  onLogoClick,
}: {
  compact?: boolean;
  onSimulate?: () => void;
  onLogoClick?: () => void;
}) {
  return (
    <header
      style={{
        position: "relative",
        height: 48,
        minHeight: 48,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: compact ? "0 12px" : "0 18px",
        borderBottom: "1px solid var(--line-2)",
        background: "var(--bg-0)",
        gap: 12,
      }}
    >
      {onLogoClick ? (
        <button
          type="button"
          onClick={onLogoClick}
          aria-label="Back to overview"
          style={{
            background: "transparent",
            border: "none",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          }}
        >
          <CompositeLogo height={compact ? 26 : 30} />
        </button>
      ) : (
        <CompositeLogo height={compact ? 26 : 30} />
      )}
      {!compact && (
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
          }}
        >
          <BetaNotice />
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <SimulateButton onClick={onSimulate} compact={compact} />
        <ConnectButton />
      </div>
    </header>
  );
}
